import React, { useState } from 'react';

interface DonationFormProps {
  formData: {
    donorName: string;
    donorEmail: string;
    donorMessage: string;
    recipientDesignation: string;
    recipientEmail: string;
    giftType: string;
    donationAmount: string;
  };
  onFormChange: (data: Partial<DonationFormProps['formData']>) => void;
  onSubmit: () => void;
  serverError?: string; // Add prop for server errors
}

// Type declaration for window.MSStream
declare global {
  interface Window {
    MSStream?: any;
  }
}

// Maximum character limit for the message field (Stripe metadata limit)
const MAX_MESSAGE_LENGTH = 500;

const DonationForm: React.FC<DonationFormProps> = ({ formData, onFormChange, onSubmit, serverError }) => {
  // Add state for field errors
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  
  // Detect if device is iOS
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { id, value } = e.target;
    
    // If it's the message field, limit to MAX_MESSAGE_LENGTH characters
    if (id === 'donorMessage') {
      // Only update if within character limit
      if (value.length <= MAX_MESSAGE_LENGTH) {
        onFormChange({ [id]: value });
      }
    } else {
      onFormChange({ [id]: value });
    }
    
    // Mark field as touched
    setTouched(prev => ({ ...prev, [id]: true }));
    
    // Clear error when user types
    if (errors[id]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[id];
        return newErrors;
      });
    }
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const { id, value } = e.target;
    setTouched(prev => ({ ...prev, [id]: true }));
    validateField(id, value);
  };

  const validateField = (id: string, value: string) => {
    if (!value.trim()) {
      setErrors(prev => ({ ...prev, [id]: 'Aquest camp és obligatori' }));
      return false;
    }
    
    if (id === 'donorEmail' || id === 'recipientEmail') {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(value)) {
        setErrors(prev => ({ ...prev, [id]: 'Email no vàlid' }));
        return false;
      }
    }
    
    if (id === 'donorMessage' && value.length > MAX_MESSAGE_LENGTH) {
      setErrors(prev => ({ ...prev, [id]: `El missatge no pot superar els ${MAX_MESSAGE_LENGTH} caràcters` }));
      return false;
    }
    
    if (id === 'donorName' && value.length > 35) {
      setErrors(prev => ({ ...prev, [id]: 'El nom no pot superar els 35 caràcters' }));
      return false;
    }
    
    return true;
  };

  // Set gift type to 'un-regal' by default if not already set
  React.useEffect(() => {
    if (!formData.giftType) {
      onFormChange({ giftType: 'un-regal' });
    }
  }, [formData.giftType, onFormChange]);

  const handleAmountClick = (amount: string) => {
    // Special handling for iOS
    if (isIOS) {
      // Focus the input first to make sure the keyboard doesn't appear
      const input = document.getElementById('donationAmount') as HTMLInputElement;
      if (input) {
        input.focus();
        // Set the amount
        onFormChange({ donationAmount: amount });
        // Blur immediately to hide keyboard
        setTimeout(() => input.blur(), 50);
      }
    } else {
      onFormChange({ donationAmount: amount });
    }
    
    // Clear any donation amount error
    if (errors.donationAmount) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors.donationAmount;
        return newErrors;
      });
    }
  };

  const handleAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Remove non-numeric characters
    const amount = e.target.value.replace(/[^\d]/g, '');
    
    // Ensure that the amount buttons are deselected when entering a custom amount
    // Even if the amount happens to match one of the predefined values, we want
    // to consider it a custom amount because the user manually entered it
    onFormChange({ donationAmount: amount });
    
    // Clear any donation amount error if value is not empty
    if (amount && errors.donationAmount) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors.donationAmount;
        return newErrors;
      });
    }
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};
    let isValid = true;
    
    // Validate all required fields
    const requiredFields = [
      { id: 'donorName', message: 'El nom del donant és obligatori' },
      { id: 'donorEmail', message: 'L\'email del donant és obligatori' },
      { id: 'recipientDesignation', message: 'El nom del destinatari és obligatori' },
      { id: 'recipientEmail', message: 'L\'email del destinatari és obligatori' },
      { id: 'donorMessage', message: 'El missatge personalitzat és obligatori' },
    ];
    
    requiredFields.forEach(field => {
      const value = formData[field.id as keyof typeof formData];
      if (!value || typeof value === 'string' && !value.trim()) {
        newErrors[field.id] = field.message;
        isValid = false;
      }
    });
    
    // Special validation for donation amount
    if (!formData.donationAmount) {
      newErrors.donationAmount = 'La quantitat a donar és obligatòria';
      isValid = false;
    }
    
    // Email validation
    const emailFields = ['donorEmail', 'recipientEmail'];
    emailFields.forEach(field => {
      const value = formData[field as keyof typeof formData];
      if (value && typeof value === 'string') {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(value)) {
          newErrors[field] = 'Format d\'email no vàlid';
          isValid = false;
        }
      }
    });
    
    // Message length validation
    if (formData.donorMessage && formData.donorMessage.length > MAX_MESSAGE_LENGTH) {
      newErrors.donorMessage = `El missatge no pot superar els ${MAX_MESSAGE_LENGTH} caràcters`;
      isValid = false;
    }
    
    // Donor name length validation
    if (formData.donorName && formData.donorName.length > 35) {
      newErrors.donorName = 'El nom no pot superar els 35 caràcters';
      isValid = false;
    }
    
    setErrors(newErrors);
    setTouched(Object.keys(formData).reduce((acc, key) => ({ ...acc, [key]: true }), {}));
    
    return isValid;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      // Focus the first field with an error
      const firstErrorField = Object.keys(errors)[0];
      if (firstErrorField) {
        const input = document.getElementById(firstErrorField) as HTMLInputElement;
        if (input) {
          input.focus();
        }
      }
      return; // Prevent form submission
    }
    
    onSubmit();
  };

  // Helper function to determine input class
  const getInputClassName = (fieldName: string) => {
    return touched[fieldName] && errors[fieldName] ? 'error-input' : '';
  };

  // Calculate remaining characters for message
  const remainingChars = MAX_MESSAGE_LENGTH - (formData.donorMessage?.length || 0);
  const isMessageNearLimit = remainingChars <= 50;

  return (
    <form id="donation-form" onSubmit={handleSubmit}>
      <div className="form-section enhanced-form-section">
        <h3 className="section-title enhanced-section-title">
          La teva informació
        </h3>
        
        <div className="form-row">
          <div className="form-group">
            <label htmlFor="donorName" className="required-field">Indica el teu nom</label>
            <input 
              type="text" 
              id="donorName" 
              placeholder="El teu nom"
              value={formData.donorName}
              onChange={handleInputChange}
              onBlur={handleBlur}
              required
              maxLength={35}
              className={getInputClassName('donorName')}
            />
            {touched.donorName && errors.donorName && (
              <div className="error-message">{errors.donorName}</div>
            )}
          </div>
          
          <div className="form-group">
            <label htmlFor="donorEmail" className="required-field">Indica el teu email</label>
            <input 
              type="email" 
              id="donorEmail" 
              placeholder="correu@gmail.com"
              required
              value={formData.donorEmail}
              onChange={handleInputChange}
              onBlur={handleBlur}
              className={getInputClassName('donorEmail')}
            />
            {touched.donorEmail && errors.donorEmail && (
              <div className="error-message">{errors.donorEmail}</div>
            )}
          </div>
        </div>
      </div>
      
      <div className="form-section enhanced-form-section">
        <h3 className="section-title enhanced-section-title">
          Informació del destinatari de la rosa Digital
        </h3>

        <div id="recipient-fields">
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="recipientDesignation" className="required-field">Indica el nom del destinatari</label>
              <input 
                type="text" 
                id="recipientDesignation" 
                placeholder="Nom del destinatari"
                value={formData.recipientDesignation}
                onChange={handleInputChange}
                onBlur={handleBlur}
                required
                className={getInputClassName('recipientDesignation')}
              />
              {touched.recipientDesignation && errors.recipientDesignation && (
                <div className="error-message">{errors.recipientDesignation}</div>
              )}
            </div>
            
            <div className="form-group">
              <label htmlFor="recipientEmail" className="required-field">Indica l'email del destinatari</label>
              <input 
                type="email" 
                id="recipientEmail" 
                placeholder="correu@gmail.com"
                required
                value={formData.recipientEmail}
                onChange={handleInputChange}
                onBlur={handleBlur}
                className={getInputClassName('recipientEmail')}
              />
              {touched.recipientEmail && errors.recipientEmail && (
                <div className="error-message">{errors.recipientEmail}</div>
              )}
            </div>
          </div>
          
          <div className="form-group">
            <label htmlFor="donorMessage" className="required-field">
              Introdueix el teu missatge personalitzat
              <span className={`char-counter ${isMessageNearLimit ? 'char-counter-warning' : ''}`}>
                {remainingChars} caràcters restants
              </span>
            </label>
            <input 
              type="text" 
              id="donorMessage" 
              placeholder="El teu missatge de Sant Jordi"
              value={formData.donorMessage}
              onChange={handleInputChange}
              onBlur={handleBlur}
              required
              maxLength={MAX_MESSAGE_LENGTH}
              className={`${getInputClassName('donorMessage')} ${isMessageNearLimit ? 'near-limit' : ''}`}
            />
            {touched.donorMessage && errors.donorMessage && (
              <div className="error-message">{errors.donorMessage}</div>
            )}
          </div>
        </div>
      </div>
      
      <div className="form-section enhanced-form-section">
        <h3 className="section-title enhanced-section-title">
          Quantitat a donar
        </h3>
        
        <div className="form-group">
          <label htmlFor="donationAmount" className="required-field">Tria quant vols donar:</label>
          <div className="donation-amounts">
            <button 
              type="button" 
              className={`amount-btn ${formData.donationAmount === '5' ? 'selected' : ''}`}
              onClick={() => handleAmountClick('5')}
            >
              5€
            </button>
            <button 
              type="button" 
              className={`amount-btn ${formData.donationAmount === '10' ? 'selected' : ''}`}
              onClick={() => handleAmountClick('10')}
            >
              10€
            </button>
            <button 
              type="button" 
              className={`amount-btn ${formData.donationAmount === '20' ? 'selected' : ''}`}
              onClick={() => handleAmountClick('20')}
            >
              20€
            </button>
            <button 
              type="button" 
              className={`amount-btn ${formData.donationAmount === '50' ? 'selected' : ''}`}
              onClick={() => handleAmountClick('50')}
            >
              50€
            </button>
          </div>
          <div className="custom-amount">
            <input 
              type="text" 
              id="donationAmount" 
              placeholder="Altra quantitat" 
              value={formData.donationAmount}
              onChange={handleAmountChange}
              onBlur={handleBlur}
              required
              className={`${formData.donationAmount ? 'selected' : ''} ${getInputClassName('donationAmount')}`}
            />
            <span className="currency">€</span>
          </div>
          <ul
              className="logos-list"
              style={{
                listStyle: "none",
                padding: 0,
                margin: 0,
                display: "flex",
                flexWrap: "wrap",
                gap: "10px",
                justifyContent: "center",
                alignItems: "center",
                marginTop: "15px"
              }}
            >
              <li>
                <img
                  className="border"
                  src="https://cdn.shopify.com/s/files/1/0638/9453/files/Visa.svg"
                  width="35"
                  height="24"
                  alt="Visa"
                  title="Visa"
                  loading="lazy"
                  style={{
                    backgroundColor: "#fff",
                    border: "1px solid #a6a6a6",
                    borderRadius: "4px"
                  }}
                />
              </li>
              <li>
                <img
                  className="border"
                  src="https://cdn.shopify.com/s/files/1/0638/9453/files/Mastercard.svg"
                  width="35"
                  height="24"
                  alt="Mastercard"
                  title="Mastercard"
                  loading="lazy"
                  style={{
                    backgroundColor: "#fff",
                    border: "1px solid #a6a6a6",
                    borderRadius: "4px"
                  }}
                />
              </li>
              <li>
                <img
                  src="https://cdn.shopify.com/s/files/1/0638/9453/files/Amex.svg"
                  width="35"
                  height="24"
                  alt="American Express"
                  title="American Express"
                  loading="lazy"
                  style={{
                    backgroundColor: "#fff",
                    border: "1px solid #a6a6a6",
                    borderRadius: "4px"
                  }}
                />
              </li>
              <li>
                <img
                  className="border"
                  src="https://cdn.shopify.com/s/files/1/0638/9453/files/PayPal.svg"
                  width="38"
                  height="24"
                  alt="Paypal"
                  title="Paypal"
                  loading="lazy"
                  style={{
                    backgroundColor: "#fff",
                    border: "1px solid #a6a6a6",
                    borderRadius: "4px"
                  }}
                />
              </li>
              <li>
                <img
                  className="border"
                  src="https://cdn.shopify.com/s/files/1/0638/9453/files/icon-apple.svg"
                  width="38"
                  height="24"
                  alt="Apple pay"
                  title="Apple pay"
                  loading="lazy"
                  style={{
                    backgroundColor: "#fff",
                    border: "1px solid #a6a6a6",
                    borderRadius: "4px"
                  }}
                />
              </li>
              <li>
                <img
                  src="https://cdn.shopify.com/s/files/1/0638/9453/files/icon-klarna.svg"
                  width="38"
                  height="24"
                  alt="Pago en 3 plazos con Klarna"
                  title="Pago en 3 plazos con Klarna"
                  loading="lazy"
                  style={{
                    backgroundColor: "#fff",
                    border: "1px solid #a6a6a6",
                    borderRadius: "4px"
                  }}
                />
              </li>
            </ul>
          {touched.donationAmount && errors.donationAmount && (
            <div className="error-message">{errors.donationAmount}</div>
          )}
        </div>
      </div>
      
      {/* Show server error if present - moved to bottom of form */}
      {serverError && (
        <div className="server-error-message">
          <div className="error-icon">⚠️</div>
          <div className="error-content">
            <strong>Error:</strong> {serverError}
          </div>
        </div>
      )}
      
      <button 
        className="submit-payment-button" 
        type="submit"
      >
        Procedir al pagament
      </button>
    </form>
  );
};

export default DonationForm; 
